import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type CouponFilterState = {
  searchTerm: string;
  activation: string[];
  status: string[];
  discountType: string[];
  durationUnit: string[];
};

type CouponFiltersProps = {
  filters: CouponFilterState;
  onFiltersChange: (filters: CouponFilterState) => void;
  counts?: {
    activation?: Record<string, number>;
    status?: Record<string, number>;
    discountType?: Record<string, number>;
    durationUnit?: Record<string, number>;
  };
};

type TabType = "resumo" | "ativacao" | "status" | "desconto" | "duracao";

export function CouponFilters({ filters, onFiltersChange, counts }: CouponFiltersProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("resumo");

  const filterCount =
    filters.activation.length +
    filters.status.length +
    filters.discountType.length +
    filters.durationUnit.length;

  const clearAll = () => {
    onFiltersChange({ ...filters, activation: [], status: [], discountType: [], durationUnit: [] });
  };

  const removeArrayItem = (
    type: keyof Pick<CouponFilterState, "activation" | "status" | "discountType" | "durationUnit">,
    value?: string,
  ) => {
    const current = (filters as any)[type] as string[];
    onFiltersChange({ ...filters, [type]: value ? current.filter((v) => v !== value) : [] });
  };

  const tabs: Array<{ id: TabType; label: string; icon: string }> = [
    { id: "resumo", label: "Resumo", icon: "📋" },
    { id: "ativacao", label: "Ativação", icon: "⚙️" },
    { id: "status", label: "Status", icon: "📊" },
    { id: "desconto", label: "Desconto", icon: "🏷️" },
    { id: "duracao", label: "Duração", icon: "⏳" },
  ];

  const getTabCount = (tabId: TabType) => {
    switch (tabId) {
      case "ativacao": return filters.activation.length;
      case "status": return filters.status.length;
      case "desconto": return filters.discountType.length;
      case "duracao": return filters.durationUnit.length;
      default: return 0;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip open={!open ? undefined : false}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="outline" className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
                {filterCount > 0 && (
                  <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center" variant="default">
                    {filterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Filtre a listagem de cupons</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="w-[520px] p-0" align="start">
        <div className="flex h-[420px]">
          <div className="w-52 border-r bg-muted/30 p-2 overflow-y-auto">
            {tabs.map((tab) => {
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1",
                    activeTab === tab.id ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </span>
                  {count > 0 && (
                    <Badge variant={activeTab === tab.id ? "secondary" : "default"} className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {count}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex-1 p-4 overflow-y-auto">
            {activeTab === "resumo" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Resumo dos Filtros</h3>
                  {filterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearAll}>Limpar tudo</Button>
                  )}
                </div>
                {filterCount === 0 ? (
                  <p className="text-muted-foreground text-sm">Nenhum filtro aplicado</p>
                ) : (
                  <div className="space-y-3">
                    {filters.activation.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Ativação</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.activation.map((v) => (
                            <Badge key={v} variant="secondary" className="gap-1 capitalize">
                              {v}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeArrayItem("activation", v)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {filters.status.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Status</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.status.map((v) => (
                            <Badge key={v} variant="secondary" className="gap-1 capitalize">
                              {v}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeArrayItem("status", v)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {filters.discountType.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Tipo de Desconto</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.discountType.map((v) => (
                            <Badge key={v} variant="secondary" className="gap-1">
                              {v}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeArrayItem("discountType", v)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {filters.durationUnit.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Duração</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.durationUnit.map((v) => (
                            <Badge key={v} variant="secondary" className="gap-1">
                              {v === "months" ? "meses" : "dias"}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeArrayItem("durationUnit", v)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "ativacao" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Tipos de Ativação</h3>
                {["regular","cortesia","testers","promocao"].map((opt) => (
                  <div key={opt} className="flex items-center space-x-2">
                    <Checkbox
                      id={`act-${opt}`}
                      checked={filters.activation.includes(opt)}
                      onCheckedChange={(checked) => {
                        const next = checked ? [...filters.activation, opt] : filters.activation.filter((v) => v !== opt);
                        onFiltersChange({ ...filters, activation: next });
                      }}
                    />
                    <Label htmlFor={`act-${opt}`} className="capitalize flex-1 flex items-center gap-2">
                      {opt}
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.activation?.[opt] ?? 0}</Badge>
                    </Label>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "status" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status</h3>
                {["active","inactive"].map((opt) => (
                  <div key={opt} className="flex items-center space-x-2">
                    <Checkbox
                      id={`st-${opt}`}
                      checked={filters.status.includes(opt)}
                      onCheckedChange={(checked) => {
                        const next = checked ? [...filters.status, opt] : filters.status.filter((v) => v !== opt);
                        onFiltersChange({ ...filters, status: next });
                      }}
                    />
                    <Label htmlFor={`st-${opt}`} className="capitalize flex-1 flex items-center gap-2">
                      {opt}
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.status?.[opt] ?? 0}</Badge>
                    </Label>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "desconto" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Tipo de Desconto</h3>
                {["full","percentage","fixed","none"].map((opt) => (
                  <div key={opt} className="flex items-center space-x-2">
                    <Checkbox
                      id={`dc-${opt}`}
                      checked={filters.discountType.includes(opt)}
                      onCheckedChange={(checked) => {
                        const next = checked ? [...filters.discountType, opt] : filters.discountType.filter((v) => v !== opt);
                        onFiltersChange({ ...filters, discountType: next });
                      }}
                    />
                    <Label htmlFor={`dc-${opt}`} className="capitalize flex-1 flex items-center gap-2">
                      {opt}
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.discountType?.[opt] ?? 0}</Badge>
                    </Label>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "duracao" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Duração</h3>
                {["days","months"].map((opt) => (
                  <div key={opt} className="flex items-center space-x-2">
                    <Checkbox
                      id={`du-${opt}`}
                      checked={filters.durationUnit.includes(opt)}
                      onCheckedChange={(checked) => {
                        const next = checked ? [...filters.durationUnit, opt] : filters.durationUnit.filter((v) => v !== opt);
                        onFiltersChange({ ...filters, durationUnit: next });
                      }}
                    />
                    <Label htmlFor={`du-${opt}`} className="capitalize flex-1 flex items-center gap-2">
                      {opt === "months" ? "meses" : "dias"}
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.durationUnit?.[opt] ?? 0}</Badge>
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

